'use client';

import { useEffect, useRef, useState } from 'react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { cn } from '@/lib/utils';
import { loadPdfjs } from '@/lib/pdf/load-pdfjs';

/** `%PDF-` — the magic bytes every PDF starts with (ISO 32000-1 §7.5.2). */
const PDF_MAGIC = '%PDF-';

/**
 * pdf.js's viewer stylesheet, served from `public/pdfjs/` by
 * `scripts/sync-pdfjs-assets.mjs` rather than imported through the bundler.
 *
 * It has to be *served*: 39 of its rules reference `url(images/…)` relative to
 * the stylesheet, and a bundler reads a bare `images/checkmark.svg` as a module
 * request and fails to resolve it. Those rules are all annotation-editor and
 * comment chrome this component never mounts, so the assets are dead weight —
 * but the stylesheet is one file and pdf.js is the only thing that can keep the
 * text layer's rules in step with the DOM and custom properties it emits. A
 * hand-copied subset would drift on the next version bump and misalign
 * selection against the glyphs, silently, because the canvas underneath would
 * still look right.
 */
const PDF_VIEWER_STYLESHEET = '/pdfjs/pdf_viewer.css';

/**
 * Add the stylesheet once, on first use, and leave it there — resolving only
 * once it has actually applied.
 *
 * Loading it lazily rather than from the root layout keeps ~250 KB of CSS off
 * every page in the app that never opens a document, and the `<link>` is
 * idempotent so any number of concurrent previews share one (the module-level
 * promise below is what makes a second concurrent call join the first rather
 * than appending a second `<link>` or racing its own `load` listener). It is
 * deliberately never removed: previews open and close constantly, and a
 * stylesheet that came and went would repaint every open one.
 *
 * **Callers must await this before `PDFViewer.setDocument()`, not just call it
 * (VALE-964).** `.pdfViewer .page` is the *only* place this stylesheet defines
 * `--user-unit` / `--total-scale-factor` / `--scale-round-x` / `--scale-round-y`,
 * and pdf.js sizes every page div inline with a `round(down, var(--total-scale-factor)
 * * …, var(--scale-round-x))` expression built from them. Before the stylesheet
 * has actually loaded — true for the very first preview opened in a session,
 * cold — those custom properties are undefined, the `round()` expression is
 * invalid, and the page div's `clientHeight` collapses to 0. pdf.js's own
 * visibility computation (`getVisibleElements`) then finds zero visible pages,
 * so `PDFViewer.update()` returns before ever queuing a render — and nothing
 * afterwards re-triggers `update()` (no resize or scroll follows), so the
 * preview is stuck on its loading state forever: no error, no failed request,
 * because nothing failed — pdf.js is correctly rendering nothing to render.
 * Every later preview in the same session finds the stylesheet already loaded
 * and renders on the first `update()` call, which is why only the first
 * document after a hard refresh ever shows it.
 */
let viewerStylesheetPromise: Promise<void> | null = null;

function ensureViewerStylesheet(): Promise<void> {
  if (typeof document === 'undefined') return Promise.resolve();
  if (viewerStylesheetPromise) return viewerStylesheetPromise;

  const existing = document.querySelector<HTMLLinkElement>(
    `link[href="${PDF_VIEWER_STYLESHEET}"]`
  );
  if (existing) {
    // `.sheet` is non-null only once the stylesheet has actually loaded and
    // applied — a `<link>` already in the DOM may still be in flight.
    viewerStylesheetPromise = existing.sheet
      ? Promise.resolve()
      : new Promise((resolve) => {
          existing.addEventListener('load', () => resolve(), { once: true });
          // A load error must not hang every future preview — the page still
          // renders (without the annotation/comment chrome this component
          // never mounts anyway), just without this stylesheet's rules.
          existing.addEventListener('error', () => resolve(), { once: true });
        });
    return viewerStylesheetPromise;
  }

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = PDF_VIEWER_STYLESHEET;
  viewerStylesheetPromise = new Promise((resolve) => {
    link.addEventListener('load', () => resolve(), { once: true });
    link.addEventListener('error', () => resolve(), { once: true });
  });
  document.head.appendChild(link);
  return viewerStylesheetPromise;
}

/**
 * The preview's own fetch was refused, carrying the `Response` that refused it.
 *
 * Several panes render a document their backend produces *on demand*, so the
 * request can come back with a refusal worth reading — which merge fields the
 * matter is missing, that there is no representative to address, that PDF
 * conversion is down. Those panes used to fetch the bytes themselves purely to
 * get at that body, then hand the preview a `blob:` URL of what they had already
 * read (VALE-914). Handing the unread response out here is what lets them stop:
 * the preview does the one fetch, and the host still gets the refusal.
 *
 * The body is **unread** — nothing consumes it before this is thrown — so a host
 * may call `.json()` on it exactly once.
 */
export class DocumentPreviewFetchError extends Error {
  constructor(readonly response: Response) {
    super(`Preview fetch failed: ${response.status}`);
    this.name = 'DocumentPreviewFetchError';
  }
}

/**
 * **What pdf.js reserves when it fits a page to the width of its container.**
 *
 * `PDFViewer._setScale` computes `page-width` as
 * `(container.clientWidth - SCROLLBAR_PADDING) / pageWidth`, and
 * `SCROLLBAR_PADDING` is a flat `40` in `pdf_viewer.mjs` — a guess at the width
 * of a scrollbar that might appear, made before the container is measured.
 *
 * Restated here because the constant is not exported, and because it is the
 * input to {@link fitPageToWidth}'s correction rather than a value we choose.
 * It is checked by `document-preview-fit.test.ts`, which reads the number back
 * out of the installed package so a pdf.js bump cannot move it silently.
 */
const PDFJS_SCROLLBAR_PADDING = 40;

/**
 * **The mat a `flush` preview leaves, total across both sides, in CSS px**
 * (VALE-1584).
 *
 * Deliberately not 0: the pages carry a 1px border and a shadow, and a page
 * sized to the exact content box puts both on the clip boundary. 8px a side
 * clears them and still reads as a page filling its frame.
 */
const FLUSH_PAGE_FIT_INSET = 16;

/**
 * Fit the document to its container.
 *
 * `page-width` alone is pdf.js's own answer and stays the default — it is the
 * only way to ask what "fits" means for this document, since the page geometry
 * varies per file and is not otherwise reachable.
 *
 * A `flush` caller then corrects it. pdf.js reserves
 * {@link PDFJS_SCROLLBAR_PADDING} for a scrollbar that might appear, and on a
 * scroll container whose `clientWidth` already excludes one that room is
 * reserved twice; reading the resulting scale back and re-scaling it by the
 * ratio of the two insets re-fits the page to the mat we want, using only the
 * viewer's public scale accessors.
 *
 * Guarded on a container narrower than pdf.js's own reserve, where the
 * correction would divide by zero or flip negative — a container that small has
 * nothing legible in it anyway, so the honest answer is to leave the fit alone.
 */
function fitPageToWidth(
  viewer: { currentScaleValue: string; currentScale: number },
  container: HTMLElement,
  fit: DocumentPreviewFit
): void {
  viewer.currentScaleValue = 'page-width';

  if (fit !== 'flush') return;

  const width = container.clientWidth;
  if (width <= PDFJS_SCROLLBAR_PADDING) return;

  viewer.currentScale =
    viewer.currentScale *
    ((width - FLUSH_PAGE_FIT_INSET) / (width - PDFJS_SCROLLBAR_PADDING));
}

/**
 * How close the page sits to its frame.
 *
 * **`default` is pdf.js's own fit, and is what every embedded preview gets**
 * — the templates workspace, the estate-account statement, the composer's
 * generated attachment, the discovery drafting pane. They sit their page in a
 * `p-3` or `p-6` mat and the reserve reads as part of that mat.
 *
 * **`flush` is the documents drawer alone** (VALE-1584). That drawer is the one
 * surface where the page is the column rather than a panel inside one, so the
 * same reserve read as the document floating away from its own frame. Opt-in
 * rather than the default precisely so the other five do not move.
 */
export type DocumentPreviewFit = 'default' | 'flush';

/**
 * Renders an embedded document preview without the browser's PDF plugin.
 *
 * Every embedded preview in the app used to be an `<iframe>` pointed at the file.
 * Chrome renders a framed PDF with its *internal plugin*, so a browser configured to
 * hand PDFs to an external application — the `AlwaysOpenPdfExternally` enterprise
 * policy, or the per-user "Download PDFs" setting, both common in law firms
 * standardised on Acrobat — has nothing to render with, and paints "This content is
 * blocked. Contact the site owner to fix the issue." over every preview surface in
 * the product (VALE-891).
 *
 * pdf.js parses the bytes in JavaScript and paints to a `<canvas>` in our own
 * document, so no plugin is involved and no browser configuration can suppress it.
 * That also covers the other causes of the same symptom — a PDF-handling extension,
 * or a web filter — none of which the plugin path survived either.
 *
 * **It previews images too, which is why it is not called `PdfPreview`.** Several
 * surfaces (the grant document, the discovery file panel) accept "a PDF or an image"
 * and do not know which they hold; the old iframe rendered whichever the browser
 * could. This fetches the bytes once and branches on the magic bytes, so those call
 * sites keep working without threading a MIME type through from their callers.
 *
 * `src` accepts anything `fetch` can reach: a same-origin `/api/proxy/...` URL, a
 * `blob:` URL, or a presigned S3 URL (the `vale-law-firms-*` buckets allow the app
 * origins in their CORS rules, which a frame navigation never needed but a fetch
 * does). Do **not** append PDF open-parameters — those addressed the browser's
 * viewer chrome, which no longer exists here.
 *
 * **The pages are laid out by pdf.js's own `PDFViewer`, not by hand (VALE-927).**
 * The first cut of this component drew each page to a canvas and appended them
 * flush, which cost three things at once, all of them invisible until someone
 * read a real multi-page document in it:
 *
 * 1. **No page breaks.** Pages butted edge to edge on a single white container,
 *    so a will's backsheet read as a continuation of its attestation clause.
 *    Separated pages are pdf.js's *default* — there is no seamless mode, and
 *    mozilla/pdf.js#6618 is someone asking how to remove the gaps — so the
 *    continuous look was something this component had to go out of its way to
 *    produce. It is not a look anyone wanted.
 * 2. **No text layer.** A canvas is a picture of a document: a solicitor could
 *    not select a clause, copy an account number, or find a beneficiary by name
 *    in any preview in the product. `PDFViewer` renders pdf.js's text layer over
 *    each page, so all three work.
 * 3. **Every page rasterised on open**, and again on every resize. A forty-page
 *    bank-statement bundle rendered forty canvases before the drawer was usable.
 *    `PDFViewer` renders the pages near the viewport and nothing else.
 *
 * Reaching for the library's own viewer rather than growing our loop is the
 * point: page geometry, scroll position, the text layer's transform and the
 * render queue are one interlocking problem, and pdf.js already solves it. What
 * stays ours is presentation — the page chrome, set through the custom
 * properties pdf.js exposes for exactly that (`--page-margin`, `--page-border`)
 * rather than by overriding its rules.
 */
export function DocumentPreview({
  src,
  title,
  className,
  fit = 'default',
  onError,
  onLoad,
  onResponse,
  'data-testid': testId = 'document-preview'
}: {
  src: string;
  title: string;
  className?: string;
  /** How close the page sits to its frame. See {@link DocumentPreviewFit}. */
  fit?: DocumentPreviewFit;
  /**
   * Notified when the document cannot be rendered, so a host can offer its own
   * fallback. A refused fetch arrives as a {@link DocumentPreviewFetchError},
   * whose `response` body is unread and is the host's to interpret.
   */
  onError?: (error: Error) => void;
  /**
   * Fired once the document has painted — the `<iframe onLoad>` equivalent, kept so
   * hosts that show their own spinner behind the preview (LF-5155) still know when to
   * drop it.
   */
  onLoad?: () => void;
  /**
   * Handed the successful `Response` before its body is read, for a host that
   * needs a header the document came with, such as the sample letter's
   * `X-Letterhead-Source` (VALE-1633). Without it a host would have to fetch the
   * same document a second time just to read one header.
   */
  onResponse?: (response: Response) => void;
  /** Defaults to `document-preview`; call sites that replaced an iframe keep their own id. */
  'data-testid'?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const documentRef = useRef<PDFDocumentProxy | null>(null);
  // The live `PDFViewer`, held so the resize effect can re-apply the fit-to-width
  // scale. Typed loosely because the class is only ever imported dynamically —
  // naming its type statically would pull `pdf_viewer.mjs` into the module graph
  // and undo that.
  const viewerInstanceRef = useRef<{
    currentScaleValue: string;
    /** Read back after a `page-width` fit, then nudged. See {@link fitPageToWidth}. */
    currentScale: number;
    setDocument: (pdf: PDFDocumentProxy | null) => void;
    cleanup: () => void;
  } | null>(null);
  /*
    `fit` is presentation, so the document-load effect must not key on it — that
    effect is keyed on `src` and re-running it would re-download the file. The
    resize effect below re-fits on a change instead, which is the only thing a
    change actually needs.

    Synced in the effect below rather than assigned during render, with the
    other three latest-value refs: a render-phase `fitRef.current = fit` is a
    ref write during render, which React Compiler refuses
    (`react-hooks/refs`). Nothing reads it any earlier than the effect commits:
    the only reader is `pagesinit`, a pdf.js event that cannot fire before the
    document it belongs to has loaded.
  */
  const fitRef = useRef(fit);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // Held in refs rather than depended on: hosts pass these inline, so a new
  // function identity every render would refetch and re-rasterise the whole
  // document on each parent render. The refs keep the latest callback without
  // making it a dependency of the load effect.
  const onLoadRef = useRef(onLoad);
  const onErrorRef = useRef(onError);
  const onResponseRef = useRef(onResponse);
  // `title` is held here for the same reason, and it matters more than it looks:
  // it feeds `renderPages`, which the load effect depends on, so a host passing a
  // title derived from query data (`document?.name ?? 'Preview'`) would refetch
  // and re-rasterise the whole document on every loading/error/cache-miss
  // transition. Every call site today passes a literal, so this is latent — but
  // the coupling is invisible from the call site, which is what makes it worth
  // closing rather than documenting.
  const titleRef = useRef(title);
  useEffect(() => {
    onLoadRef.current = onLoad;
    onErrorRef.current = onError;
    onResponseRef.current = onResponse;
    titleRef.current = title;
    fitRef.current = fit;
  });

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    let teardown: (() => void) | null = null;

    async function load() {
      setState('loading');
      setImageUrl(null);
      try {
        const response = await fetch(src);
        if (!response.ok) {
          throw new DocumentPreviewFetchError(response);
        }
        onResponseRef.current?.(response);
        const bytes = await response.arrayBuffer();
        if (cancelled) return;

        const header = new TextDecoder().decode(
          bytes.slice(0, PDF_MAGIC.length)
        );

        if (header !== PDF_MAGIC) {
          // Not a PDF — an image, on the surfaces that accept either. Hand it to the
          // browser as-is, which is exactly what the iframe did for these.
          objectUrl = URL.createObjectURL(
            new Blob([bytes], {
              type:
                response.headers.get('content-type') ??
                'application/octet-stream'
            })
          );
          setImageUrl(objectUrl);
          setState('ready');
          onLoadRef.current?.();
          return;
        }

        // Kicked off here rather than awaited immediately: it overlaps the
        // stylesheet's network fetch with the dynamic imports and document
        // parsing below, so the warm case (every open but the first in a
        // session) pays nothing extra — the promise is already resolved by
        // the time it is actually awaited, right before `setDocument()`.
        const viewerStylesheetReady = ensureViewerStylesheet();

        // Imported dynamically: pdf.js is ~1MB and touches browser-only APIs, so it
        // stays out of both the server render and the initial client bundle.
        /*
          **The `legacy/` build, not the default one — this is load-bearing from
          pdfjs 6 onward.** v6 refactored `EventBus` and much of the core onto
          `Map.prototype.getOrInsertComputed`, a TC39 Stage-3 method no shipping
          browser implements yet. The default build only *consumes* it; only the
          `legacy/` build carries the core-js polyfill that defines it (see
          `legacy/build/pdf.mjs`, "`Map.prototype.getOrInsertComputed` method").

          On the default build the preview therefore dies at `viewer.setDocument`
          with `TypeError: this[#listeners].getOrInsertComputed is not a function`
          — and nothing else catches it: it type-checks, builds, and passes the
          unit suite, because the failure only exists in a real browser. The
          backend's pdfjs call sites have always used `legacy/` (for the ESM/CJS
          reason documented there) which is why they were unaffected.

          Revisit only once the method is baseline across our supported browsers.
        */
        //
        // The worker is served from `public/` rather than resolved through the
        // bundler: the package's own worker import uses a `webpackIgnore` magic
        // comment Turbopack does not honour, and a pnpm workspace would not hoist
        // it where a `new URL(..., import.meta.url)` reference could find it
        // either. Both live in `loadPdfjs`, shared with the trust statement review.
        const pdfjs = await loadPdfjs();

        /*
          **`pdf_viewer.mjs` takes the library as a GLOBAL, and reads it while it
          evaluates.** Its line 109 is a bare
          `const { AbortException, … } = globalThis.pdfjsLib`, at module scope —
          it is a webpack bundle built to sit beside a `<script>` tag, not an ES
          module that imports its own dependency.

          Two consequences, and both have to hold:

          1. The global must be assigned **before** the import statement runs.
          2. The two imports must be **sequential**. Loading them in a
             `Promise.all` — which is what this did first — starts both
             evaluations in the same tick, so the viewer can reach its
             destructure before `pdfjsLib` is set and dies with
             `Cannot destructure property 'AbortException' of
             'globalThis.pdfjsLib' as it is undefined`. That failure is a race,
             so it presents as an intermittently broken preview rather than a
             consistent one, which is how it survived a first look.

          Assigning the global is not a workaround for the bundler; it is this
          entry point's actual interface.
        */
        (globalThis as typeof globalThis & { pdfjsLib?: unknown }).pdfjsLib =
          pdfjs;
        // `legacy/web/…` for the same polyfill reason as the core import above —
        // the viewer's `EventBus` is the very class that needs it.
        const viewerModule =
          await import('pdfjs-dist/legacy/web/pdf_viewer.mjs');

        const pdf = await pdfjs.getDocument({ data: bytes }).promise;
        // `pdf.loadingTask.destroy()`, not `pdf.destroy()`: pdfjs 6 removed the
        // latter from `PDFDocumentProxy`. It was only ever an alias — v5's body is
        // literally `return this.loadingTask.destroy()` — so this is the same call,
        // spelled the way v6 still exposes it.
        if (cancelled) {
          void pdf.loadingTask.destroy();
          return;
        }

        const container = containerRef.current;
        const viewerEl = viewerRef.current;
        if (!container || !viewerEl) {
          void pdf.loadingTask.destroy();
          return;
        }

        documentRef.current = pdf;

        // Must be applied before `setDocument()` below — see the doc comment
        // on `ensureViewerStylesheet`/VALE-964: pdf.js sizes every page div
        // from custom properties only this stylesheet defines, and computing
        // that size before the stylesheet has loaded collapses every page to
        // zero height, which stops the preview from ever rendering.
        await viewerStylesheetReady;
        if (cancelled) {
          void pdf.loadingTask.destroy();
          return;
        }

        const { EventBus, PDFLinkService, PDFViewer } = viewerModule;
        const eventBus = new EventBus();
        // Internal links (a table of contents, a cross-reference) resolve within
        // the document rather than dead-ending. It needs the viewer back, hence
        // the two-step wiring pdf.js's own examples use.
        const linkService = new PDFLinkService({ eventBus });
        const viewer = new PDFViewer({
          container,
          viewer: viewerEl,
          eventBus,
          linkService
        });
        linkService.setViewer(viewer);
        viewerInstanceRef.current =
          viewer as unknown as typeof viewerInstanceRef.current;

        // `pagesinit` is the first moment the page geometry is known, which is
        // what `page-width` needs to resolve against the container.
        const onPagesInit = () => {
          fitPageToWidth(viewer, container, fitRef.current);
        };
        // Ready is deliberately the first *painted* page, not `pagesinit`: the
        // latter fires while the pages are still blank, which would drop the
        // spinner onto an empty mat.
        const onPageRendered = () => {
          if (cancelled) return;
          setState('ready');
          onLoadRef.current?.();
        };
        eventBus.on('pagesinit', onPagesInit);
        eventBus.on('pagerendered', onPageRendered);

        viewer.setDocument(pdf);
        linkService.setDocument(pdf, null);

        teardown = () => {
          eventBus.off('pagesinit', onPagesInit);
          eventBus.off('pagerendered', onPageRendered);
          // Clearing the document is how pdf.js tears a viewer down: it
          // cancels in-flight renders, resets the page views and releases the
          // annotation editor (`pdf_viewer.mjs` `setDocument`, the `if
          // (!pdfDocument) return` branch — which pdf.js reaches by calling
          // `findController.setDocument(null)` on itself two lines earlier).
          // The shipped `.d.ts` declares the parameter non-nullable and is
          // simply narrower than the function, so the cast asserts the branch
          // that already exists rather than inventing a use for it.
          (viewer.setDocument as (pdf: PDFDocumentProxy | null) => void)(null);
          viewer.cleanup();
          viewerInstanceRef.current = null;
        };
      } catch (error) {
        if (cancelled) return;
        setState('error');
        // Trace it. A preview that fails is otherwise completely silent — the
        // host's `onError` is optional and the user just sees a grey panel, which
        // is the same dead end VALE-891 exists to remove.
        console.error('[DocumentPreview] failed to render', { src }, error);
        onErrorRef.current?.(
          error instanceof Error ? error : new Error(String(error))
        );
      }
    }

    void load();

    return () => {
      cancelled = true;
      teardown?.();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      void documentRef.current?.loadingTask.destroy();
      documentRef.current = null;
    };
  }, [src]);

  // Re-fit when the container resizes — a drawer opening, a window resize, a
  // responsive breakpoint. Re-asserting `page-width` is the whole operation now:
  // the viewer re-flows and re-renders only the pages near the viewport, where
  // the hand-rolled loop this replaced re-rasterised the entire document.
  useEffect(() => {
    const container = containerRef.current;
    if (!container || state !== 'ready' || imageUrl !== null) return;

    let frame = 0;
    let lastWidth = container.clientWidth;
    const observer = new ResizeObserver(() => {
      const width = container.clientWidth;
      if (width === 0 || width === lastWidth) return;
      lastWidth = width;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (viewerInstanceRef.current) {
          fitPageToWidth(viewerInstanceRef.current, container, fit);
        }
      });
    });
    observer.observe(container);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [state, imageUrl, fit]);

  return (
    <div
      className={cn(
        'relative',
        imageUrl !== null && 'overflow-auto',
        className
      )}
      data-testid={testId}
      // The `<iframe>` this replaced carried the document's name as its accessible
      // name; keep that on the wrapper so the surface is still announced, and still
      // reachable by name in tests.
      title={title}
      role="group"
      aria-label={title}
    >
      {imageUrl !== null ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={imageUrl} alt={title} className="block h-auto w-full" />
      ) : (
        /*
          `PDFViewer` measures this element to decide which pages are near the
          viewport, and it reads `clientHeight` — so the scroll has to be here
          rather than on the wrapper, and it has to be absolutely positioned
          inside it. Given a static, auto-height container it sees a viewport as
          tall as the whole document, concludes every page is visible, and
          renders all of them: the exact behaviour this change exists to stop.
        */
        <div
          ref={containerRef}
          className="absolute inset-0 overflow-auto"
          aria-busy={state === 'loading'}
        >
          {/*
            `pdfViewer` is pdf.js's own class and is what its stylesheet keys
            off — not decoration. The arbitrary properties beside it are the
            supported way to restyle the page chrome: pdf.js ships
            `--page-margin: 1px auto -8px` with a 9px *transparent* border, a
            trick that fakes a gap while keeping the sheet edge-to-edge. We want
            a real one, so each page becomes its own bordered, shadowed sheet
            with 12px of mat below it — the treatment `PDF_PAGE_CLASS` used to
            give the container back when the container was the page.
          */}
          <div
            ref={viewerRef}
            className={cn(
              'pdfViewer',
              /*
                **The border goes through pdf.js's own variable, not a utility.**
                `.pdfViewer .page` sets `border: var(--page-border)`, and a
                `[&_.page]:border` utility compiles to the same specificity — so
                which one wins is decided by stylesheet order, and the viewer's
                stylesheet is injected at runtime, after Tailwind's. Setting the
                variable is the package's own extension surface and cannot lose
                that race.

                **The top margin is small on purpose; the bottom one carries the
                gap.** A `0` top margin put the first page's edge exactly on the
                scroll container's clip boundary, so its border was cut off while
                the other three sides drew normally — but anything much larger
                misaligns the page against the scrollbar, whose track starts at
                the container's own top edge. 2px clears the clip without a
                visible offset.

                The space *between* pages is the bottom margin's job, and 8px is
                what makes a page break read as one. Adjacent vertical margins
                collapse, so the gap is 8px rather than 10 — one page's bottom
                and the next page's top do not add up.
              */
              /*
                `var(--border)`, the RAW token — never `var(--color-border)`.
                The `--color-*` names are a `@theme` indirection that resolves
                once at `:root` and inherits downward as a literal, so a utility
                reading through one is themed only when `.dark` sits on `<html>`
                itself and never from a scoped wrapper. `design-token-cascade-gate`
                asserts exactly this and caught it here (VALE-518).
              */
              '[--page-border:1px_solid_var(--border)]',
              '[--page-margin:2px_auto_8px]',
              '[&_.page]:bg-card [&_.page]:rounded-sm [&_.page]:shadow-sm'
            )}
          />
        </div>
      )}
      {state === 'loading' ? (
        <div className="text-muted-foreground absolute inset-0 flex items-center justify-center text-sm">
          Loading preview…
        </div>
      ) : null}
      {state === 'error' ? (
        <div className="text-muted-foreground absolute inset-0 flex items-center justify-center p-4 text-center text-sm">
          This document couldn’t be displayed.
        </div>
      ) : null}
    </div>
  );
}
